import { Controller } from "@hotwired/stimulus"

const GAME_MAP = {
  f1_24: "F1_24",
  f1_25: "F1_25",
  iracing: "IRacing",
  ams2: "AMS2",
  lmu: "LMU",
}

export default class extends Controller {
  static targets = [
    "button",
    "dropdown",
    "listbox",
    "input",
    "label",
    "preview",
    "option",
    "filter",
    "empty",
  ]
  static values = { open: Boolean, autoopen: Boolean }

  connect() {
    this.clickOutsideHandler = (e) => {
      if (this.openValue && !this.element.contains(e.target)) this.cancel()
    }
    document.addEventListener("click", this.clickOutsideHandler)
    this.gameFilter = ""
    this.textFilter = ""
    this.syncLabel()

    // Arrives lazily inside the round's inline track field: the click that
    // asked for it happened before this markup existed, so open ourselves.
    if (this.autoopenValue) this.open()
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutsideHandler)
  }

  toggle(e) {
    e.stopPropagation()
    this.openValue ? this.close() : this.open()
  }

  open() {
    this.openValue = true
    // Arrowing through the list writes each highlighted track into the hidden
    // input as a preview, so abandoning the dropdown must put back what was
    // there — otherwise a browse-then-Escape silently changes the value.
    this.valueBeforeOpen = this.inputTarget.value
    this.dropdownTarget.classList.remove("hidden")

    if (this.hasFilterTarget) {
      this.filterTarget.value = ""
      this.textFilter = ""
      this.applyVisibility()
      // Defer focus until after the dropdown paints, otherwise the click that
      // opened the dropdown can re-target focus mid-event.
      requestAnimationFrame(() => this.filterTarget.focus())
    }

    const selected = this.listboxTarget.querySelector("[aria-selected=true]")
    if (selected && !selected.classList.contains("hidden"))
      selected.scrollIntoView({ block: "nearest" })
  }

  close() {
    this.openValue = false
    this.dropdownTarget.classList.add("hidden")
  }

  // Closing without choosing: undo the arrow-key preview.
  cancel() {
    if (
      this.valueBeforeOpen !== undefined &&
      this.inputTarget.value !== this.valueBeforeOpen
    ) {
      this.inputTarget.value = this.valueBeforeOpen
      this.optionTargets.forEach((o) => {
        o.setAttribute(
          "aria-selected",
          String(o.dataset.trackSelectId === this.valueBeforeOpen),
        )
      })
      this.syncLabel()
    }
    this.close()
  }

  select(e) {
    const option = e.currentTarget
    const value = option.dataset.trackSelectId || ""

    this.inputTarget.value = value
    this.optionTargets.forEach((o) => o.setAttribute("aria-selected", "false"))
    option.setAttribute("aria-selected", "true")

    this.valueBeforeOpen = value // chosen, so there is nothing to undo
    this.syncLabel()
    this.close()
    // Nothing listens inside a plain form, but the round's inline picker
    // commits on this — a programmatic value change fires no event of its own.
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Live text filter — matches against the visible track-name span.
  // Does NOT touch aria-selected: that mark belongs to the actual selected
  // track (the form value), not to whichever row Enter would pick. Enter
  // falls back to the first visible match in keydown() if needed.
  filter() {
    this.textFilter = (this.filterTarget.value || "").trim().toLowerCase()
    this.applyVisibility()
  }

  // Called by event-track-filter when game changes.
  filterByGame(game) {
    this.gameFilter = GAME_MAP[game] || ""
    this.applyVisibility()

    // Reset selection if the currently-selected track doesn't match the new game.
    const selectedId = this.inputTarget.value
    if (selectedId) {
      const selectedOption = this.optionTargets.find(
        (o) => o.dataset.trackSelectId === selectedId,
      )
      if (selectedOption && selectedOption.classList.contains("hidden")) {
        this.inputTarget.value = ""
        this.optionTargets.forEach((o) =>
          o.setAttribute("aria-selected", "false"),
        )
        const blank = this.optionTargets.find((o) => !o.dataset.trackSelectId)
        if (blank) blank.setAttribute("aria-selected", "true")
        this.syncLabel()
      }
    }
  }

  applyVisibility() {
    let visibleCount = 0
    this.optionTargets.forEach((option) => {
      const isBlank = !option.dataset.trackSelectId
      const optionGame = option.dataset.trackSelectGame || ""
      const matchesGame =
        !this.gameFilter || isBlank || optionGame === this.gameFilter

      const nameEl = option.querySelector("[data-track-name]")
      const name = (nameEl?.textContent || "").toLowerCase()
      // Hide the "No track" entry when the user has typed something — it's
      // not a real match and only adds noise.
      const matchesText =
        !this.textFilter || (!isBlank && name.includes(this.textFilter))

      const visible = matchesGame && matchesText
      option.classList.toggle("hidden", !visible)
      if (visible) visibleCount += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
    }
  }

  visibleOptions() {
    return this.optionTargets.filter((o) => !o.classList.contains("hidden"))
  }

  keydown(e) {
    if (!this.openValue) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault()
        this.open()
      }
      return
    }

    const visible = this.visibleOptions()
    const current = visible.findIndex(
      (o) => o.getAttribute("aria-selected") === "true",
    )

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this.focusOption(visible, Math.min(current + 1, visible.length - 1))
        break
      case "ArrowUp":
        e.preventDefault()
        this.focusOption(visible, Math.max(current - 1, 0))
        break
      case "Enter":
        e.preventDefault()
        if (current >= 0) {
          visible[current].click()
        } else if (visible.length > 0) {
          // The currently-selected track is filtered out. Pick the top match.
          visible[0].click()
        }
        break
      case "Escape":
        e.preventDefault()
        this.cancel()
        this.buttonTarget.focus()
        break
    }
  }

  focusOption(visible, index) {
    if (visible.length === 0) return
    this.optionTargets.forEach((o) => o.setAttribute("aria-selected", "false"))
    visible[index].setAttribute("aria-selected", "true")
    visible[index].scrollIntoView({ block: "nearest" })
    this.syncLabel()
    this.inputTarget.value = visible[index].dataset.trackSelectId || ""
  }

  syncLabel() {
    const selectedId = this.inputTarget.value
    if (!selectedId) {
      this.labelTarget.textContent = "No track"
      this.previewTarget.innerHTML = ""
      return
    }

    const option = this.optionTargets.find(
      (o) => o.dataset.trackSelectId === selectedId,
    )
    if (!option) return

    const nameEl = option.querySelector("[data-track-name]")
    this.labelTarget.textContent = nameEl ? nameEl.textContent : ""

    const svg = option.querySelector("svg")
    this.previewTarget.innerHTML = svg ? svg.outerHTML : ""
  }
}
